/**
 * Unified BathroomsMapView Component
 *
 * This component consolidates native and web versions of the BathroomsMapView.
 * It renders a map view with bathroom locations, clustering, and floating action buttons (FABs)
 * for navigation and map reset. Supports detailed callouts for bathrooms.
 *
 * Features:
 * - Interactive map with markers and clustering.
 * - FABs for navigation and resetting the map.
 * - Detailed callouts for bathrooms locations.
 *
 * @module BathroomsMapView
 */

import React from "react";
import { useNavigation } from "@react-navigation/native";
import { Dimensions, Platform } from "react-native";
import GeneralMapView from "../../Other/MapView/GeneralMapView";
import { toTitleCase } from "../../../utils/formatting";
import BathroomsStore from "../../../storage/bathroomsStore";
import { logAnalyticsEvent } from "../../../utils/firebase";

const BathroomMarkerDisabled = require("../../../assets/images/bathroom-point-disabled.png");
const BathroomMarkerEnabled = require("../../../assets/images/bathroom-point-enabled.png");

const fixDataForMapView = (arr) => {
  return arr.map((obj) => {
    if (obj.geometry) {
      obj.longitude = obj.geometry.x;
      obj.latitude = obj.geometry.y;

      delete obj.geometry;
    }
    return obj;
  });
};

const BathroomsMapView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-BathroomsMapView" });

  const navigation = useNavigation();

  const initialCenter =
    Platform.OS === "web"
      ? { lat: 36.8506, lng: -75.9779 }
      : {
          latitude: 36.8506,
          longitude: -75.9779,
          latitudeDelta: 0.1,
          longitudeDelta: 0.1,
        };

  const bathroomsData = fixDataForMapView(BathroomsStore((state) => state.bathrooms));

  const handleNavigateToListView = () => {
    navigation.navigate("BathroomListView");
  };

  const handleNavigateToDetailView = (location) => {
    const index = bathroomsData.findIndex((bathrooms) => bathrooms.id === location.id);
    navigation.navigate("BathroomDetailsView", { index });
  };

  const calloutProps = {
    title: (location) => toTitleCase(location.attributes.Location),
    subtitle: () => null,
  };

  return (
    <GeneralMapView
      data={bathroomsData}
      initialCenter={initialCenter}
      markerIcons={
        Platform.OS === "android"
          ? {
              enabled: { uri: "bathroompointenabled" },
              disabled: { uri: "bathroompointdisabled" },
            }
          : {
              enabled: BathroomMarkerEnabled,
              disabled: BathroomMarkerDisabled,
            }
      }
      calloutProps={calloutProps}
      onNavToListView={handleNavigateToListView}
      onNavToDetailView={handleNavigateToDetailView}
    />
  );
};

export default BathroomsMapView;
